import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThemeColors, type ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { strings } from '@/constants/residents/strings';


type Variant = 'disabled' | 'future';

export const UndergoingMaintenancePage: React.FC<{ variant?: Variant }> = ({ variant = 'disabled' }) => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const theme = useThemeColors();
  const DC = useDesktopColors();
  const styles = useMemo(() => makeStyles(theme, DC), [theme.mode, DC]);

  const title = strings.maintenance.disabledTitle;
  const message =
    variant === 'future' ? strings.maintenance.futureDescription : strings.maintenance.disabledDescription;

  if (!isDesktop) {
    // Mobile view
    return (
      <View style={styles.mobileContainer}>
        <View style={styles.mobileIconContainer}>
          <RESIDENTS_SVG_COMPONENTS.HAND_WRENCH width={64} height={64} />
        </View>
        <Text style={styles.mobileTitle}>{title}</Text>
        <Text style={styles.mobileMessage}>{message}</Text>
      </View>
    );
  }

  // Desktop view
  return (
    <View style={styles.desktopContainer}>
      <View style={styles.desktopIconContainer}>
        <RESIDENTS_SVG_COMPONENTS.HAND_WRENCH width={120} height={120} />
      </View>
      <View style={styles.desktopContent}>
        <Text style={styles.desktopTitle}>{title}</Text>
        <Text style={styles.desktopMessage}>{message}</Text>
      </View>
    </View>
  );
};

type DesktopColors = ReturnType<typeof useDesktopColors>;

const makeStyles = (theme: ThemeContextValue, DC: DesktopColors) => StyleSheet.create({
  // Mobile styles
  mobileContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
    backgroundColor: theme.semantic.background.primary,
  },
  mobileIconContainer: {
    marginBottom: 32,
  },
  mobileTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.semantic.text.primary,
    textAlign: 'center',
    marginBottom: 8,
  },
  mobileMessage: {
    fontSize: 14,
    color: theme.semantic.text.tertiary,
    textAlign: 'center',
    lineHeight: 20,
  },

  // Desktop styles
  desktopContainer: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    backgroundColor: DC.PRIMARY_BACKGROUND,
    minHeight: '100%',
  },
  desktopIconContainer: {
    marginBottom: 32,
  },
  desktopContent: {
    alignItems: 'center',
    maxWidth: '80%',
    gap: 16,
  },
  desktopTitle: {
    fontSize: 32,
    fontWeight: '600',
    color: DC.TEXT_PRIMARY,
    textAlign: 'center',
  },
  desktopMessage: {
    fontSize: 18,
    color: DC.TEXT_TERTIARY,
    textAlign: 'center',
    lineHeight: 28,
    maxWidth: 600,
  },
});
